import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PersonDetail,
  PersonUpdate,
  updatePerson,
  deletePerson,
  EVENT_TYPE_LABELS,
  EventCreate,
  EventUpdate,
  createEvent,
  updateEvent,
  deleteEvent,
  EventInfo,
} from "../../api/trees";
import LoadingSpinner from "../Common/LoadingSpinner";
import DateInput from "./DateInput";

interface EditPersonFormProps {
  treeId: number;
  person: PersonDetail;
  onClose: () => void;
  onSuccess: () => void;
}

const SEX_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "U", label: "Unknown" },
];

const EVENT_TYPES = Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export default function EditPersonForm({
  treeId,
  person,
  onClose,
  onSuccess,
}: EditPersonFormProps) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Person form state
  const [formData, setFormData] = useState<PersonUpdate>({
    given_name: person.given_name,
    surname: person.surname,
    full_name: person.full_name,
    sex: person.sex,
    birth_date_text: person.birth_date_text,
    birth_place_text: person.birth_place_text,
    death_date_text: person.death_date_text,
    death_place_text: person.death_place_text,
    notes: person.notes,
  });

  // Event form state
  const [editingEvent, setEditingEvent] = useState<EventInfo | null>(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState<EventCreate>({
    event_type: "EVEN",
    date_text: "",
    place_text: "",
    description: "",
    notes: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Update person mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PersonUpdate) => {
      const token = await getAccessTokenSilently();
      return updatePerson(treeId, person.id, data, token);
    },
    onSuccess: () => {
      console.log('Update mutation succeeded!');
      // Invalidate persons list in case name changed
      queryClient.invalidateQueries({ queryKey: ["persons"] });
      
      // Call the success callback (parent will handle refetch)
      console.log('Calling onSuccess callback');
      onSuccess();
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to update person");
    },
  });

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: async (data: EventCreate) => {
      const token = await getAccessTokenSilently();
      return createEvent(treeId, person.id, data, token);
    },
    onSuccess: async () => {
      console.log('Event created successfully');
      // Invalidate the specific person query
      await queryClient.invalidateQueries({ 
        queryKey: ["person", String(treeId), String(person.id)],
        refetchType: 'active'
      });
      setShowAddEvent(false);
      setNewEvent({
        event_type: "EVEN",
        date_text: "",
        place_text: "",
        description: "",
        notes: "",
      });
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to create event");
    },
  });

  // Update event mutation
  const updateEventMutation = useMutation({
    mutationFn: async ({ eventId, data }: { eventId: number; data: EventUpdate }) => {
      const token = await getAccessTokenSilently();
      return updateEvent(treeId, eventId, data, token);
    },
    onSuccess: async () => {
      console.log('Event updated successfully');
      // Invalidate the specific person query
      await queryClient.invalidateQueries({ 
        queryKey: ["person", String(treeId), String(person.id)],
        refetchType: 'active'
      });
      setEditingEvent(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to update event");
    },
  });

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: number) => {
      const token = await getAccessTokenSilently();
      return deleteEvent(treeId, eventId, token);
    },
    onSuccess: async () => {
      console.log('Event deleted successfully');
      // Invalidate the specific person query
      await queryClient.invalidateQueries({ 
        queryKey: ["person", String(treeId), String(person.id)],
        refetchType: 'active'
      });
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to delete event");
    },
  });

  // Delete person mutation
  const deletePersonMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return deletePerson(treeId, person.id, token);
    },
    onSuccess: () => {
      // Invalidate queries and navigate back to tree
      queryClient.invalidateQueries({ queryKey: ["persons"] });
      queryClient.invalidateQueries({ queryKey: ["trees", treeId, "hierarchy"] });
      navigate(`/trees/${treeId}`);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to delete person");
    },
  });

  const handleDeletePerson = () => {
    setShowDeleteConfirm(false);
    deletePersonMutation.mutate();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    setError(null);
    updateMutation.mutate(formData);
  };

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    createEventMutation.mutate(newEvent);
  };

  const handleUpdateEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;
    setError(null);
    updateEventMutation.mutate({
      eventId: editingEvent.id,
      data: {
        event_type: editingEvent.event_type,
        date_text: editingEvent.date_text,
        place_text: editingEvent.place_text,
        description: editingEvent.description,
        notes: editingEvent.notes,
      },
    });
  };

  const handleDeleteEvent = (eventId: number) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      deleteEventMutation.mutate(eventId);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative my-8 w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-vault-400 hover:text-vault-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="font-serif text-xl font-semibold text-vault-900">Edit Person</h2>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Name Fields */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-vault-700">Given Name</label>
              <input
                type="text"
                value={formData.given_name || ""}
                onChange={(e) => setFormData({ ...formData, given_name: e.target.value })}
                className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-vault-700">Surname</label>
              <input
                type="text"
                value={formData.surname || ""}
                onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-vault-700">Sex</label>
            <select
              value={formData.sex || "U"}
              onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
              className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
            >
              {SEX_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Birth Fields */}
          <div className="rounded-lg bg-vault-50 p-4">
            <h3 className="font-medium text-vault-900">Birth</h3>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              <DateInput
                value={formData.birth_date_text || null}
                onChange={(value) => setFormData({ ...formData, birth_date_text: value })}
                label="Date"
              />
              <div>
                <label className="block text-sm text-vault-600">Place</label>
                <input
                  type="text"
                  value={formData.birth_place_text || ""}
                  onChange={(e) => setFormData({ ...formData, birth_place_text: e.target.value })}
                  placeholder="e.g., London, England"
                  className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                />
              </div>
            </div>
          </div>

          {/* Death Fields */}
          <div className="rounded-lg bg-vault-50 p-4">
            <h3 className="font-medium text-vault-900">Death</h3>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              <DateInput
                value={formData.death_date_text || null}
                onChange={(value) => setFormData({ ...formData, death_date_text: value })}
                label="Date"
              />
              <div>
                <label className="block text-sm text-vault-600">Place</label>
                <input
                  type="text"
                  value={formData.death_place_text || ""}
                  onChange={(e) => setFormData({ ...formData, death_place_text: e.target.value })}
                  placeholder="e.g., Manchester, England"
                  className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-vault-700">Notes</label>
            <textarea
              value={formData.notes || ""}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-between items-center border-t border-vault-200 pt-4">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              Delete Person
            </button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <LoadingSpinner size="sm" /> : "Save Changes"}
              </button>
            </div>
          </div>
        </form>

        {/* Events Section */}
        <div className="mt-6 border-t border-vault-200 pt-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg font-semibold text-vault-900">Events</h3>
            <button
              onClick={() => setShowAddEvent(true)}
              className="text-sm text-vault-600 hover:text-vault-900"
            >
              + Add Event
            </button>
          </div>

          {/* Add Event Form */}
          {showAddEvent && (
            <form onSubmit={handleAddEvent} className="mt-4 rounded-lg bg-vault-50 p-4">
              <h4 className="font-medium text-vault-900">New Event</h4>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm text-vault-600">Type</label>
                  <select
                    value={newEvent.event_type}
                    onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none"
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <DateInput
                  value={newEvent.date_text || null}
                  onChange={(value) => setNewEvent({ ...newEvent, date_text: value })}
                  label="Date"
                />
                <div className="sm:col-span-2">
                  <label className="block text-sm text-vault-600">Place</label>
                  <input
                    type="text"
                    value={newEvent.place_text || ""}
                    onChange={(e) => setNewEvent({ ...newEvent, place_text: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-vault-600">Description</label>
                  <input
                    type="text"
                    value={newEvent.description || ""}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddEvent(false)}
                  className="rounded-lg px-3 py-1.5 text-sm text-vault-600 hover:bg-vault-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-vault-600 px-3 py-1.5 text-sm text-white hover:bg-vault-700"
                  disabled={createEventMutation.isPending}
                >
                  {createEventMutation.isPending ? "Adding..." : "Add Event"}
                </button>
              </div>
            </form>
          )}

          {/* Events List */}
          <div className="mt-4 space-y-2">
            {person.events.length === 0 && !showAddEvent && (
              <p className="text-sm text-vault-500">No events recorded.</p>
            )}
            {person.events.map((event) => (
              <div key={event.id} className="rounded-lg border border-vault-200 p-3">
                {editingEvent?.id === event.id ? (
                  <form onSubmit={handleUpdateEvent} className="space-y-2">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <select
                        value={editingEvent.event_type}
                        onChange={(e) =>
                          setEditingEvent({ ...editingEvent, event_type: e.target.value })
                        }
                        className="w-full rounded border border-vault-300 px-2 py-1 text-sm"
                      >
                        {EVENT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <DateInput
                        value={editingEvent.date_text || null}
                        onChange={(value) =>
                          setEditingEvent({ ...editingEvent, date_text: value })
                        }
                        label="Date"
                      />
                    </div>
                    <input
                      type="text"
                      value={editingEvent.place_text || ""}
                      onChange={(e) =>
                        setEditingEvent({ ...editingEvent, place_text: e.target.value })
                      }
                      placeholder="Place"
                      className="w-full rounded border border-vault-300 px-2 py-1 text-sm"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingEvent(null)}
                        className="text-sm text-vault-600 hover:text-vault-900"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="rounded bg-vault-600 px-2 py-1 text-sm text-white hover:bg-vault-700"
                        disabled={updateEventMutation.isPending}
                      >
                        Save
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="rounded bg-vault-100 px-2 py-0.5 text-xs font-medium text-vault-700">
                        {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                      </span>
                      {event.date_text && (
                        <span className="ml-2 text-sm text-vault-500">{event.date_text}</span>
                      )}
                      {event.place_text && (
                        <p className="mt-1 text-sm text-vault-600">📍 {event.place_text}</p>
                      )}
                      {event.description && (
                        <p className="mt-1 text-sm text-vault-600">{event.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingEvent(event)}
                        className="text-vault-400 hover:text-vault-600"
                        title="Edit"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="text-vault-400 hover:text-red-600"
                        title="Delete"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-vault-900 mb-2">
                Delete {person.full_name || "this person"}?
              </h3>
              <p className="text-sm text-vault-600 mb-4">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="text-sm text-vault-600 mb-6 list-disc list-inside space-y-1">
                <li>All personal information and events</li>
                <li>All media tags for this person</li>
                <li>All family relationships (parent/child/spouse)</li>
              </ul>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                  disabled={deletePersonMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePerson}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                  disabled={deletePersonMutation.isPending}
                >
                  {deletePersonMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Deleting...
                    </span>
                  ) : (
                    "Delete Permanently"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

